/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from 'react';

import { User } from '@vimeo/viewer';

import { isVimeoRedirectableUrl } from '../../libs/isVimeoRedirectableUrl';
import { SwitchType } from './constants';

export const handleSSOLogin = (
  connectionUrl: string,
  customDomain?: string,
  redirectUrl?: string | null,
) => {
  const urlParams = new URLSearchParams(window?.parent?.location?.search);
  const redirect_param = urlParams?.get('redirect') || '';
  const prevPage = getPathName();
  const prevPageRedirect =
    '&prev_page=' +
    (redirectUrl && isVimeoRedirectableUrl(redirectUrl)
      ? redirectUrl
      : isVimeoRedirectableUrl(redirect_param) && prevPage === '/log_in'
      ? redirect_param
      : encodeURIComponent(prevPage));

  window.parent.location.href =
    (customDomain || '') + '/auth0/connect?connection=' + connectionUrl + prevPageRedirect;
};

const clipPages = ['svv_recipient_clip_page', 'showcase_recipient', 'channels', 'groups'];
export const handlePreRedirectTask = (type: SwitchType) => {
  // if user registers, track whether they joined via clip page or not
  if (type === 'join') {
    const pageName = getPageNameFromPath(window.parent.location.pathname);
    if (clipPages.includes(pageName)) {
      window.sessionStorage.setItem('reg-source', 'clip_page');
    }
  }
};

export const handleRedirect = (
  onSuccess?: (response: any) => void,
  response?: any,
  type?: string,
) => {
  if (type === 'join') {
    window.sessionStorage.setItem('upsellModal-dismissed', 'joinWithEmail'); // keeping for backward compatability, safe to remove later.
    window.localStorage.setItem('upsellModal-dismissed', 'joinWithEmail');

    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectUrl');
      if (isVimeoRedirectableUrl(redirectUrl)) {
        window.parent.location.href = redirectUrl;
        return;
      }
    }
  }
  // Default for vimeo-next pages
  if (response?.redirect_user_to_help_desk) {
    window.location.href = `https://${window.location.hostname}/help/sso?redirect_to=${response.redirect_url}`;
  } else if (onSuccess) {
    onSuccess(response);
  }
  // Default for most usecases. Redirect to the redirect_url received from api response
  else if (response.redirect_url) {
    window.parent.location.href = response.redirect_url;
  }
  // Fallback - redirect to LIHP
  else {
    window.parent.location = '/';
  }
};

export const getPageNameFromPath = (path: string, user?: User | null) => {
  const pageRegexMap = {
    '^/blog.*': 'blog',
    '^/store/ondemand/buy/.*': 'checkout_form_page_vod',
    '^/store/receipt.*': 'checkout_form_page_receipt',
    '^/store/.*': 'checkout_form_page_ss',
    '^/ondemand(/.*)?$': 'vod',
    '^/stock(/.*)?$': 'stock',
    '%/ott/pricing/calculator%': 'ott_pricing_calculation_page',
    '%/ott/pricing%': 'ott_pricing_page',
    '/welcome-survey%': 'registration_survey_page',
    '^/channels.*': 'channels',
    '/manage/folders': 'video_library',
    '/manage/videos': 'video_library',
    '/library': 'video_library',
    '/manage/(folders|team|events|showcases|webinars)/([0-9]|[a-z])+.*': `${
      path.split('/')[2]
    }_manager`,
    '/manage/videos/([0-9]|[a-z])+.*': 'single_video_view_manager',
    '%/create/templates%': 'create_homepage',
    '%/create/customize?%': 'editor',
    '%/create/trimmer%': 'create_trimmer',
    '/create/new%': 'create_wizard',
    '.*/download/.*': 'download_video_file',
    '^/security': 'marketing_landing_page_enterprise',
    '/live/(select|event/create)(/[0-9]+.*)?$': 'live_event_type_select',
    '/live/broadcaster(/event)?/[0-9]+.*': 'live_browser_studio',
    '/live/rtmp/event/[0-9]+.*': 'live_event_rtmp_page',
    '/live/registration/([0-9]|[a-z])+.*': 'event_registration_page',
    '/events?/[0-9]+.*': 'live_event_view_page',
    '/live/completed/event/[0-9]+.*': 'live_event_completed',
    '/events/.*': 'marketing_landing_page_events',
    '/integrations/.*': 'marketing_landing_page_integrations',
    '(/campaigns/|/bestoftheyear(/)?).*': 'marketing_landing_page_campaigns',
    '^/accounts/.*': 'livestream_com',
    '/experts/.*': 'experts_homepage',
    '^/for-hire(/.*)?$': 'for_hire',
    '^/ott(/.*)?$': 'ott',
    '^/help/.*': 'help',
    '^/features(/.*)?$': 'marketing_landing_page_features',
    '.*/showcase/[0-9a-zA-Z]+.*': 'showcase_recipient',
    '(^/about/.*|^/about$)': 'about',
    '.*/features(/.*)?$': 'marketing_uri_part_features',
    '^(/create|/tools|/video-editor)(/.*)?$': 'marketing_landing_page_create',
    '^/enterprise(/.*)?$': 'marketing_landing_page_enterprise',
    '.*/video-teleprompter/.*': 'marketing_landing_page_video_teleprompter',
    '/experts': 'marketing_landing_page_experts',
    '^/solutions/.*': 'marketing_landing_page_solutions',
    '^(/log_in|/login|/log_in_modal)(/.*)?$': 'login_screen',
    '^(/join|/oauth/(join|authorize)|/signup|/join_modal)(/.*)?$': 'join_screen',
    '^/analytics/.*|^/analytics$': 'analytics_page',
    '^/cancel/offer.*': 'cancel_sub_page',
    '^/library-search/.*|^/library-search$': 'library_search_result_page',
    '^/manage/event/[0-9]+.*': 'event_setting_page',
    '^/watch/.*|^/watch$': 'watch',
    '^/watchlater(/.*)?$': 'watch_later_list',
    '^/webinars/.*|^/webinars$': 'webinars',
    '^/activity/.*|^/activity$': 'activity_page',
    '^/logged_out/.*|^/logged_out$': 'log_out_page',
    '^/categories/.*|^/categories$': 'categories_page',
    '^/vindiciapaypalomnipay/.*|^/vindiciapaypalomnipay$': 'vindicia_pay',
    '^/apps/237719/preview/.*|^//apps/237719/preview/$': 'apps_preview_page',
    '^/publisher/.*|^/publisher$': 'logged_in_homepage_publish_modal_opened',
    '^/embed-email/.*|^/embed-email$': 'logged_in_homepage_embed_email_modal_opened',
    '.*.wirewax.com.*|.*wirewax.com$': 'interactive',
    '^/share/resource.*': 'share_resource_page',
    '^/_facebook/join/.*': 'facebook_join_page',
    '^/_facebook/login/.*': 'facebook_login_page',
    '^/forgot_password.*': 'forgot_password',
    '^/record/.*': 'record',
    '^/_google/join/.*': 'google_join_page',
    '^/_google/login/.*': 'google_login_page',
    '^/_facebook/login/.': 'facebook_login_page',
    '(.*/user|.*/user[0-9]+.*)': 'user_profile',
    '^/settings/.*': 'user_settings',
    '^/groups/.*': 'groups',
    '^/upload(/.*)?$': 'upload_page',
    '(/review/[0-9]+.*|%/review/%)': 'review_page',
    '(.*/videos/[0-9]+.*|.*/[0-9]+.*|/clip)': 'clip_page',
    '^(/upgrade|/upgrade-plan|/pricing|/sign_up|/welcome)(/.*)?$': 'upgrade_page',
    '(/watch/.*|/watch$)': 'watch',
    '^/$|^/vmlp|^/#|(/home(/.*)?)$': user ? 'logged_in_home_page' : 'logged_out_homepage',
  };

  for (const regexPattern in pageRegexMap) {
    const regex = new RegExp(regexPattern);
    if (regex.test(path)) {
      return pageRegexMap[regexPattern];
    }
  }
  return 'other';
};

export function getPathName() {
  const path =
    window.self === window.top
      ? window.document.location.pathname
      : document.referrer.replace(/^[^:]+:\/\/[^/]+/, '').replace(/#.*/, '');

  return path;
}

export function getRedirectAndTypeFromQueryParams(params: URLSearchParams): {
  redirect: string | null;
  modalType: string;
  socialLoginRedirect: string | null;
  socialJoinRedirect: string | null;
} {
  const redirect = params?.get('redirect');
  const socialLoginRedirect = params?.get('redirect_url_after_social_login');
  const socialJoinRedirect = params?.get('redirect_url_after_social_join');
  const modalType = params?.get('type') || 'join';
  return { redirect, modalType, socialLoginRedirect, socialJoinRedirect };
}

export function useIsModalInIframe() {
  const [isFramed, setIsFramed] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsFramed(window.self !== window.top);
    }
  }, []);

  return isFramed;
}

export function getReferrerPath(): string {
  const referrer = document.referrer;
  const origin = window.location.origin;
  if (referrer.startsWith(origin)) {
    return referrer.replace(origin, '');
  }
  return '/';
}
